// Стили формы держим здесь, а не в экране-обёртке: форма живёт ещё и в
// модалке на лендинге, где AuthLayout не монтируется и css не подключался.
import './auth.css';
import { type FormEvent } from 'react';
import { DSButton } from '@app/components/DSButton';
import {
  hasSocialAuth,
  isAppleEnabled,
  isGoogleEnabled,
} from '@app/services/social-auth.service';
import { usePhoneAuth } from './usePhoneAuth';

// Провайдеры включаются env-переменными на этапе сборки — читаем один раз.
const appleEnabled = isAppleEnabled();
const googleEnabled = isGoogleEnabled();
// Пока провайдеры не сконфигурированы, блок соцсетей — мёртвый UI: прячем.
const socialEnabled = hasSocialAuth();

export interface PhoneAuthFormProps {
  /** Колбэк после успешного входа (закрытие модалки/редирект). */
  onSuccess?: () => void;
  /** Показывать ли кнопку «Назад» на шаге code (default true). */
  showBackButton?: boolean;
}

/**
 * Переиспользуемая Phone+SMS форма.
 *
 * Шаги: phone → code → (success | blocked).
 * Используется в:
 *   - AuthModal (модалка в Navbar)
 *   - AuthLayout (/app/auth — direct URL)
 */
export function PhoneAuthForm({
  onSuccess,
  showBackButton = true,
}: PhoneAuthFormProps) {
  const auth = usePhoneAuth({ onSuccess });

  if (auth.step === 'success') {
    return (
      <div className="auth-success-shell">
        <div className="auth-success-icon">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="auth-success-title">Вы вошли</h2>
        <p className="auth-success-subtitle">Перенаправляем…</p>
      </div>
    );
  }

  if (auth.step === 'blocked') {
    return (
      <div className="auth-success-shell">
        <div
          className="auth-success-icon"
          style={{
            background: 'color-mix(in srgb, var(--error) 22%, transparent)',
            color: 'var(--error)',
          }}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M5 5l14 14" />
          </svg>
        </div>
        <h2 className="auth-success-title">Аккаунт заблокирован</h2>
        <p className="auth-success-subtitle">
          Свяжитесь со службой поддержки: info@aura.app
        </p>
      </div>
    );
  }

  return (
    <form
      className="auth-form"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (auth.step === 'phone') {
          auth.sendCode(auth.phone);
        } else {
          auth.verifyCode(auth.code.join(''));
        }
      }}
    >
      {auth.step === 'phone' ? (
        <>
          <label className="auth-input-label" htmlFor="phone">
            Телефон
          </label>
          <input
            id="phone"
            className={`auth-input ${auth.error ? 'auth-input-error' : ''}`}
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            value={auth.phone}
            onChange={(e) => auth.setPhone(e.target.value)}
            placeholder="+7 999 123-45-67"
            disabled={auth.loading}
            autoFocus
          />
          {auth.error && <p className="auth-error-text">{auth.error}</p>}

          <DSButton
            type="submit"
            variant="primary"
            size="large"
            disabled={auth.loading}
            className="auth-submit"
          >
            {auth.loading ? <span className="auth-spinner" /> : 'Получить код'}
          </DSButton>

          {socialEnabled && (
            <>
              <div className="auth-divider">или войдите через</div>
              <div className="auth-altMethods">
                <button
                  type="button"
                  className="auth-altMethod"
                  onClick={() => auth.signInWithProvider('apple')}
                  disabled={!appleEnabled || auth.loading}
                  title={
                    appleEnabled
                      ? 'Войти через Apple'
                      : 'Будет доступно после настройки Apple Services ID'
                  }
                >
                  <svg
                    className="auth-altMethodIcon"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M16.36 1.43c.05 1.06-.36 2.1-1.06 2.86-.74.78-1.94 1.38-3.12 1.3-.07-1.04.4-2.12 1.06-2.84.74-.78 2-1.36 3.12-1.32zM20.6 17.36c-.5 1.16-.74 1.68-1.4 2.7-.9 1.43-2.18 3.2-3.76 3.21-1.4.02-1.76-.92-3.66-.91-1.9.01-2.3.93-3.7.91-1.58-.03-2.78-1.62-3.7-3.05C1.96 16.76 1.68 12.4 3.3 10.06c.86-1.24 2.22-2 3.46-2 1.27 0 2.07.85 3.12.85 1.02 0 1.64-.86 3.1-.86 1.12 0 2.3.61 3.15 1.66-2.77 1.52-2.32 5.48.46 6.65z" />
                  </svg>
                  Apple
                </button>
                <button
                  type="button"
                  className="auth-altMethod"
                  onClick={() => auth.signInWithProvider('google')}
                  disabled={!googleEnabled || auth.loading}
                  title={
                    googleEnabled
                      ? 'Войти через Google'
                      : 'Будет доступно после настройки Google OAuth Web Client'
                  }
                >
                  <svg className="auth-altMethodIcon" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09 0-.73.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
                    />
                  </svg>
                  Google
                </button>
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <div className="auth-codeHint">
            <p className="auth-codeHintText">
              Код отправлен на <strong>{auth.phone}</strong>
            </p>
          </div>

          <div className="auth-codeGrid">
            {auth.code.map((digit, i) => (
              <input
                key={i}
                id={`code-${i}`}
                className={`auth-codeCell ${auth.error ? 'auth-codeCell-error' : ''}`}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={digit}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(-1);
                  auth.setCode((prev) => {
                    const next = [...prev];
                    next[i] = val;
                    return next;
                  });
                  if (val && i < 3) {
                    const nextInput = document.getElementById(
                      `code-${i + 1}`
                    ) as HTMLInputElement | null;
                    nextInput?.focus();
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Backspace' && !auth.code[i] && i > 0) {
                    const prevInput = document.getElementById(
                      `code-${i - 1}`
                    ) as HTMLInputElement | null;
                    prevInput?.focus();
                  }
                }}
                onPaste={(e) => {
                  const pasted = e.clipboardData
                    .getData('text')
                    .replace(/\D/g, '')
                    .slice(0, 4);
                  if (pasted.length > 1) {
                    e.preventDefault();
                    const next = ['', '', '', ''];
                    pasted.split('').forEach((c, idx) => {
                      if (idx < 4) next[idx] = c;
                    });
                    auth.setCode(next);
                    const lastFilled = Math.min(pasted.length, 4) - 1;
                    const target = document.getElementById(
                      `code-${lastFilled}`
                    ) as HTMLInputElement | null;
                    target?.focus();
                  }
                }}
                disabled={auth.loading}
                autoFocus={i === 0}
                aria-label={`Цифра ${i + 1}`}
              />
            ))}
          </div>

          {auth.error && <p className="auth-error-text">{auth.error}</p>}

          <DSButton
            type="submit"
            variant="primary"
            size="large"
            disabled={auth.loading}
            className="auth-submit"
          >
            {auth.loading ? <span className="auth-spinner" /> : 'Подтвердить'}
          </DSButton>

          <div className="auth-resendRow">
            Не получили код?
            <button
              type="button"
              className="auth-resendBtn"
              onClick={() => auth.sendCode(auth.phone)}
              disabled={auth.loading}
            >
              Отправить ещё раз
            </button>
          </div>
          {showBackButton && (
            <button
              type="button"
              className="auth-resendBtn"
              onClick={auth.goBackToPhone}
              disabled={auth.loading}
            >
              ← Изменить номер
            </button>
          )}
        </>
      )}
    </form>
  );
}
